/**
 * RunnerAuditModal — iter303aj-fix7
 * ─────────────────────────────────
 * Modal displayed when operator clicks "Audit Status" or "Audit Runner".
 * Fetches static-analysis findings from
 *   GET /api/status-workflows/audit/{status_key}   (single-status audit)
 *   GET /api/status-workflows/audit                (runner-wide audit)
 * and renders them grouped by status.
 *
 * Findings shape (from backend):
 *   { severity: 'info'|'warn'|'error', code, message,
 *     status_key, task_id, action_id, action_type }
 *
 * Props:
 *   scope         'status' | 'runner'
 *   statusKey     (only when scope='status')
 *   onClose       fn()
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, ShieldAlert, ShieldCheck, ShieldOff, ClipboardCopy, Loader2 } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const SEV_META = {
  error: { Icon: ShieldOff,   cls: 'text-rose-700 bg-rose-50 border-rose-200',   label: 'Error'   },
  warn:  { Icon: ShieldAlert, cls: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Warning' },
  info:  { Icon: ShieldCheck, cls: 'text-sky-700 bg-sky-50 border-sky-200',       label: 'Info'    },
};

const SEV_ORDER = ['error', 'warn', 'info'];

export default function RunnerAuditModal({ scope, statusKey, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const url = scope === 'runner'
        ? '/status-workflows/audit'
        : `/status-workflows/audit/${statusKey}`;
      const res = await api.get(url);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [scope, statusKey]);

  useEffect(() => { load(); }, [load]);

  const findings = data?.findings || [];
  const totals   = data?.totals || data?.counts || { error: 0, warn: 0, info: 0 };
  const perStatus = data?.per_status || (data && [{
    status_key: data.status_key,
    status_label: data.status_label,
    task_mode: data.task_mode,
    rollup: data.rollup,
    counts: data.counts,
  }]);

  // Group findings by status for display.
  const grouped = {};
  for (const f of findings) {
    grouped[f.status_key] = grouped[f.status_key] || [];
    grouped[f.status_key].push(f);
  }
  // Sort each group by severity.
  Object.keys(grouped).forEach(k => grouped[k].sort(
    (a, b) => SEV_ORDER.indexOf(a.severity) - SEV_ORDER.indexOf(b.severity)
  ));

  const copyToClipboard = () => {
    const lines = [
      `Runner Audit · scope=${scope}${statusKey ? ' · '+statusKey : ''}`,
      `Totals: ${totals.error || 0} errors · ${totals.warn || 0} warnings · ${totals.info || 0} info`,
      '',
    ];
    for (const p of perStatus || []) {
      lines.push(`▶ ${p.status_label} · mode=${p.task_mode} · wired=${p.rollup?.wired_pct || 0}% · counts={E:${p.counts?.error || 0} W:${p.counts?.warn || 0} I:${p.counts?.info || 0}}`);
      for (const f of grouped[p.status_key] || []) {
        lines.push(`   ${f.severity.toUpperCase()} · ${f.code} · ${f.message}`);
      }
      lines.push('');
    }
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         data-testid="wf-audit-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <ShieldCheck size={20} className="text-sky-600" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">
              {scope === 'runner' ? 'Runner-wide Audit' : `Status Audit — ${data?.status_label || statusKey}`}
            </div>
            <div className="text-[11px] text-slate-500">
              Static analysis of status_workflows templates + primitive registry
            </div>
          </div>
          <button onClick={copyToClipboard}
            data-testid="wf-audit-copy"
            className="text-xs text-slate-500 hover:text-sky-600 flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-50">
            <ClipboardCopy size={12} /> {copied ? 'Copied!' : 'Copy report'}
          </button>
          <button onClick={onClose} data-testid="wf-audit-close"
            className="text-slate-400 hover:text-slate-700 p-1"><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
              <Loader2 className="animate-spin" size={16} /> Running audit…
            </div>
          )}
          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{error}</div>
          )}

          {!loading && !error && data && (
            <>
              {/* Summary tiles */}
              <div className="grid grid-cols-3 gap-3 mb-5" data-testid="wf-audit-summary">
                {['error', 'warn', 'info'].map(sev => {
                  const M = SEV_META[sev];
                  return (
                    <div key={sev} className={`border rounded-lg p-3 ${M.cls}`}>
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold">
                        <M.Icon size={12} /> {M.label}s
                      </div>
                      <div className="text-2xl font-bold mt-1">{totals[sev] || 0}</div>
                    </div>
                  );
                })}
              </div>

              {/* Per-status rollup */}
              {scope === 'runner' && perStatus && (
                <div className="mb-5" data-testid="wf-audit-perstatus">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
                    Per-status rollup
                  </div>
                  <div className="border border-slate-200 rounded overflow-hidden">
                    {perStatus.map(p => (
                      <div key={p.status_key}
                        className="flex items-center gap-3 px-3 py-2 text-xs border-b last:border-b-0 border-slate-100 hover:bg-slate-50">
                        <div className="w-40 font-semibold text-slate-700">{p.status_label}</div>
                        <div className="w-24 text-slate-500">mode: <span className="font-mono">{p.task_mode}</span></div>
                        <div className="flex-1">
                          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div className="bg-emerald-500 h-full" style={{ width: `${p.rollup?.wired_pct || 0}%` }} />
                          </div>
                        </div>
                        <div className="w-14 text-right text-slate-500 font-mono">{p.rollup?.wired_pct || 0}%</div>
                        <div className="flex items-center gap-1 w-20 justify-end">
                          {p.counts?.error > 0 && <span className="text-rose-600 text-[10px] font-bold">E:{p.counts.error}</span>}
                          {p.counts?.warn > 0 && <span className="text-amber-600 text-[10px] font-bold">W:{p.counts.warn}</span>}
                          {p.counts?.info > 0 && <span className="text-sky-600 text-[10px] font-bold">I:{p.counts.info}</span>}
                          {p.counts?.error === 0 && p.counts?.warn === 0 && p.counts?.info === 0 &&
                            <span className="text-emerald-600 text-[10px] font-bold">clean</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Findings list */}
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
                Findings ({findings.length})
              </div>
              {findings.length === 0 ? (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center gap-2">
                  <ShieldCheck size={16} /> No design issues detected — the audit is clean.
                </div>
              ) : (
                <div className="space-y-2" data-testid="wf-audit-findings">
                  {Object.entries(grouped).map(([sk, fs]) => (
                    <div key={sk} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold text-slate-600 uppercase tracking-widest border-b border-slate-100">
                        {sk.replace('_', ' ')} · {fs.length} finding{fs.length === 1 ? '' : 's'}
                      </div>
                      <div className="divide-y divide-slate-100">
                        {fs.map((f, idx) => {
                          const M = SEV_META[f.severity] || SEV_META.info;
                          return (
                            <div key={idx} className="px-3 py-2 flex items-start gap-2 text-xs">
                              <M.Icon size={14} className={`mt-0.5 ${M.cls.split(' ')[0]}`} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${M.cls}`}>
                                    {f.code}
                                  </span>
                                  {f.action_type && (
                                    <span className="text-[10px] font-mono text-slate-500">
                                      type: {f.action_type}
                                    </span>
                                  )}
                                </div>
                                <div className="text-slate-700 mt-1">{f.message}</div>
                                {(f.task_id || f.action_id) && (
                                  <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                                    {f.task_id && <span>task: {f.task_id.slice(0, 8)}…</span>}
                                    {f.action_id && <span className="ml-2">action: {f.action_id.slice(0, 8)}…</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
